import { z } from "zod";
import {
    ScheduleMatchResolutionKind,
    ScheduleSourceType,
} from "@common/types/domains/accounting/schedule";
import { scheduleSourceTypeSchema } from "./schedule.schema";

export enum ScheduleLinkedSourceErrorCode {
    linkedSourceScheduled = "linked_source_scheduled",
    linkedSourceScheduledBulk = "linked_source_scheduled_bulk",
    scheduleSourceMove = "schedule_source_move",
}

export const scheduleLinkedApplicationSchema = z.object({
    applicationId: z.string().uuid(),
    sourceType: scheduleSourceTypeSchema,
    transactionId: z.number().int().nullable(),
    invoiceId: z.string().uuid().nullable(),
    billId: z.string().uuid().nullable(),
    scheduleName: z.string(),
    termStart: z.string().date(),
    amount: z.number().nullable(),
    /** null covers the whole source. */
    sourceLineIds: z.array(z.string().uuid()).nullable(),
});

export type ScheduleLinkedApplication = z.infer<
    typeof scheduleLinkedApplicationSchema
>;

/** One transaction–invoice link whose both ends carry a live schedule. */
export const matchScheduleConflictSchema = z.object({
    transactionId: z.number().int(),
    invoiceId: z.string().uuid(),
    lineItemIds: z.array(z.string().uuid()),
    matchedAmount: z.number(),
    transactionApplication: scheduleLinkedApplicationSchema,
    invoiceApplications: z.array(scheduleLinkedApplicationSchema).min(1),
    /**
     * The match would split the transaction, which a scheduled transaction
     * cannot be, so its schedule cannot be the one kept.
     */
    willSplitTransaction: z.boolean().optional(),
});

export type MatchScheduleConflict = z.infer<typeof matchScheduleConflictSchema>;

export const scheduleMatchResolutionSchema = z.object({
    invoiceId: z.string().uuid(),
    resolution: z.nativeEnum(ScheduleMatchResolutionKind),
    /** Empty means every transaction in conflict with this invoice. */
    transactionIds: z.array(z.number().int()).default([]),
});

export type ScheduleMatchResolution = z.infer<
    typeof scheduleMatchResolutionSchema
>;

/**
 * Returned (409) when a payment transaction and the invoice or bill it pays
 * would both carry a live schedule, which recognizes the same amount twice. `requested`
 * is the schedule being applied; it is null when the conflict surfaces while
 * matching two sources that are both already scheduled.
 */
export const scheduleLinkedSourceConflictErrorSchema = z.object({
    errorCode: z.literal(ScheduleLinkedSourceErrorCode.linkedSourceScheduled),
    message: z.string(),
    existing: z.array(scheduleLinkedApplicationSchema).min(1),
    /** Per-link detail, present when the conflict surfaces while matching. */
    conflicts: z.array(matchScheduleConflictSchema).min(1).optional(),
    requested: z
        .object({
            sourceType: scheduleSourceTypeSchema,
            scheduleName: z.string(),
            termStart: z.string().date(),
        })
        .nullable(),
});

export type ScheduleLinkedSourceConflictErrorBody = z.infer<
    typeof scheduleLinkedSourceConflictErrorSchema
>;

export const scheduleSourceMoveSchema = z.object({
    applicationId: z.string().uuid(),
    transactionId: z.number().int(),
    documentId: z.string().uuid(),
    documentType: z.enum([ScheduleSourceType.bill, ScheduleSourceType.invoice]),
    lineItemIds: z.array(z.string().uuid()),
    amount: z.number(),
    scheduleName: z.string(),
});

/**
 * Returned (409) when matching would move a payment's schedule onto the bill
 * or invoice. Nothing is written until the request repeats with
 * `move_to_document`.
 */
export const scheduleSourceMoveConflictErrorSchema = z.object({
    errorCode: z.literal(ScheduleLinkedSourceErrorCode.scheduleSourceMove),
    message: z.string(),
    moves: z.array(scheduleSourceMoveSchema).min(1),
});

export type ScheduleSourceMoveConflictErrorBody = z.infer<
    typeof scheduleSourceMoveConflictErrorSchema
>;

export const scheduleBulkLinkedSourceConflictErrorSchema = z
    .object({
        errorCode: z.literal(
            ScheduleLinkedSourceErrorCode.linkedSourceScheduledBulk,
        ),
        message: z.string(),
        transactionIds: z.array(z.number().int()),
        invoiceIds: z.array(z.string().uuid()),
    })
    .refine((body) => body.transactionIds.length + body.invoiceIds.length > 0, {
        message: "At least one conflicting source is required",
    });

export type ScheduleBulkLinkedSourceConflictErrorBody = z.infer<
    typeof scheduleBulkLinkedSourceConflictErrorSchema
>;

export const listScheduleLinkedSourcesQuerySchema = z
    .object({
        entityId: z.coerce.number().int().positive(),
        invoiceId: z.string().uuid().optional(),
        transactionId: z.coerce.number().int().positive().optional(),
    })
    .refine(
        (query) =>
            (query.invoiceId === undefined) !==
            (query.transactionId === undefined),
        { message: "Pass exactly one of invoiceId or transactionId" },
    );

export const listScheduleLinkedSourcesResponseSchema = z.object({
    existing: z.array(scheduleLinkedApplicationSchema),
});
